import { EChannelType, TMember, TQuickReplyItem, TSendMessage } from 'types';
import { QUICK_REPLY_QUESTION_MESSAGE_KIND, TEXT_STRING } from '../constants';

type ChatPublishClient = {
  publish: (payload: {
    channel: string;
    message: Record<string, unknown>;
  }) => Promise<unknown>;
  sendFile: (payload: {
    channel: string;
    message: Record<string, unknown>;
    file: File;
  }) => Promise<unknown>;
};

export type SendChatMessageDeps = {
  payload: TSendMessage;
  pubnub: ChatPublishClient;
  channel: string;
  channelType?: string;
  userName?: string;
  userUuid?: string;
  users: TMember[];
  onCreateChannel?: () => Promise<{ id?: string } | undefined>;
  mutatePushNotification: (body: {
    channelId: string;
    channelType: string;
    message: string;
    title: string;
    hasNotification?: boolean;
    messageKind?: 'QUICK_REPLY_QUESTION';
  }) => void;
  mutateLineChatMessage: (body: {
    senderName: string;
    channelId: string;
    channelType: string;
    channelParticipant: Array<{
      id: number;
      name: string;
      provider: TMember['provider'];
      lineUserId?: string;
    }>;
  }) => void;
  scheduleNormal: () => void;
  flushQuestion: () => Promise<boolean>;
};

export const sendChatMessage = async ({
  payload,
  pubnub,
  channel,
  channelType,
  userName,
  userUuid,
  users,
  onCreateChannel,
  mutatePushNotification,
  mutateLineChatMessage,
  scheduleNormal,
  flushQuestion,
}: SendChatMessageDeps): Promise<void> => {
  const { message, file, gift, media } = payload;
  const isQuestion = payload.messageKind === QUICK_REPLY_QUESTION_MESSAGE_KIND;
  if (!file && !message.trim() && !gift && !media?.length) return;

  let messageBody = `${message.trim()}\n`;
  if (file) messageBody += TEXT_STRING.MESSAGE.IMAGE;
  if (media?.length) messageBody += TEXT_STRING.MESSAGE.VIDEO;
  if (gift)
    messageBody += TEXT_STRING.COMMON.QUOTE.replace('$value', gift.name);
  const notificationTitle = `${userName || ''} ${
    TEXT_STRING.COMMON.RECEIVED_MESSAGE
  }`;
  const messagePayload = {
    text: message,
    ...(isQuestion
      ? { data: { messageKind: QUICK_REPLY_QUESTION_MESSAGE_KIND } }
      : {
          pn_gcm: {
            notification: {
              title: notificationTitle,
              body: messageBody,
              sound: 'default',
            },
            data: {
              key: channel,
              type: channelType,
            },
          },
        }),
    gift,
    ...(media?.length ? { media } : {}),
  };

  let targetChannel = channel;
  if (onCreateChannel) {
    const newChannel = await onCreateChannel();
    if (newChannel?.id) {
      targetChannel = newChannel.id;
    }
  }

  if (file) {
    await pubnub.sendFile({
      channel: targetChannel,
      message: messagePayload,
      file,
    });
  } else if (message || gift || media?.length) {
    await pubnub.publish({
      channel: targetChannel,
      message: messagePayload,
    });
  }

  const questionSent = isQuestion ? flushQuestion() : undefined;
  if (questionSent) {
    void questionSent.catch(() => undefined);
  }
  if (!isQuestion) {
    scheduleNormal();
  }

  mutatePushNotification({
    channelId: channel,
    channelType: channelType || '',
    message: messageBody,
    title: notificationTitle,
    ...(isQuestion
      ? {
          hasNotification: false,
          messageKind: QUICK_REPLY_QUESTION_MESSAGE_KIND,
        }
      : {}),
  });

  if (
    ![EChannelType.PRIVATE, EChannelType.SALON].includes(
      channelType as EChannelType,
    )
  ) {
    mutateLineChatMessage({
      senderName: userName || TEXT_STRING.LINE.UNKWOWN,
      channelId: channel,
      channelType: channelType || '',
      channelParticipant: users.map((member) => ({
        id: member.id,
        name: member.name,
        provider: member.provider,
        lineUserId: member.lineUserId,
      })),
    });
  }

  const channelUsers = users.filter((item) => item.uuid !== userUuid);
  if (channelUsers.length > 0) {
    await Promise.all(
      channelUsers.map(({ uuid }) =>
        pubnub.publish({
          channel: uuid,
          message: {
            channel,
            text: messageBody,
          },
        }),
      ),
    );
  }

  if (isQuestion) {
    const sentOk = await questionSent;
    if (!sentOk) {
      throw new Error('channel activity update failed');
    }
  }
};

export const submitQuickReplyQuestion = async ({
  item,
  sendMessage,
  startAnswering,
  sendAnswer,
}: {
  item: TQuickReplyItem;
  sendMessage: (payload: TSendMessage) => Promise<void>;
  startAnswering: (quickReplyId: string) => void;
  sendAnswer: () => Promise<unknown>;
}): Promise<void> => {
  await sendMessage({
    message: item.suggestText,
    messageKind: QUICK_REPLY_QUESTION_MESSAGE_KIND,
  });
  startAnswering(item.id);
  await sendAnswer();
};
